// src/components/common/DragDropFile.tsx

import { useRef, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import * as XLSX from 'xlsx';
import { Box, Button, Stack, Typography } from '@mui/material';

/** Default upload limits (short-term OOM guard; backend remains the real safeguard). */
const DEFAULT_MAX_SIZE_MB = 50;
const DEFAULT_MAX_ROWS = 50000;

type DragDropFileProps = {
  /** Called when user confirms the selected file */
  onConfirmFile: (file: File) => void;
  /** Max file size in MB (default 50). */
  maxSizeMb?: number;
  /** Max data row count excluding the header (default 50000). */
  maxRows?: number;
};

/**
 * DragDropFile
 *
 * A lightweight drag & drop uploader for a single file.
 * - Keeps UI minimal and MUI-only
 * - Does NOT auto-upload on selection (user must confirm)
 * - Supports both drag-drop and "Choose File"
 * - Validates file size and row count to block oversized uploads (OOM guard)
 */
const DragDropFile = ({
  onConfirmFile,
  maxSizeMb = DEFAULT_MAX_SIZE_MB,
  maxRows = DEFAULT_MAX_ROWS,
}: DragDropFileProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Token to ignore stale async validation results when a new file is selected.
  const validationToken = useRef(0);

  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);

  /**
   * Validate file size (sync) then row count (parse).
   * Size is checked first so an oversized file is never parsed in the browser.
   */
  const validateFile = async (selected: File): Promise<string | null> => {
    const maxBytes = maxSizeMb * 1024 * 1024;
    if (selected.size > maxBytes) {
      const sizeMb = (selected.size / (1024 * 1024)).toFixed(1);
      return `File is too large (${sizeMb}MB / max ${maxSizeMb}MB).`;
    }

    try {
      const buf = await selected.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const ref = sheet?.['!ref'];
      if (ref) {
        const range = XLSX.utils.decode_range(ref);
        // exclude the header row
        const rowCount = range.e.r - range.s.r;
        if (rowCount > maxRows) {
          return `Too many rows (${rowCount.toLocaleString()} / max ${maxRows.toLocaleString()} rows).`;
        }
      }
    } catch {
      // Parsing failed (e.g. not a valid xlsx); let the backend validate the template.
      return null;
    }

    return null;
  };

  /** Set the selected file and run validation, ignoring stale results. */
  const selectFile = (selected: File) => {
    const token = ++validationToken.current;
    setFile(selected);
    setError(null);
    setValidating(true);

    void validateFile(selected).then((result) => {
      // Ignore if a newer file was selected meanwhile.
      if (token !== validationToken.current) return;
      setError(result);
      setValidating(false);
    });
  };

  /** Treat enter/over as active, leave as inactive. */
  const handleDrag = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragleave') {
      setDragActive(false);
      return;
    }

    setDragActive(true);
  };

  /** Accept the first file only (single-file upload). */
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const dropped = e.dataTransfer.files?.[0];
    if (!dropped) return;

    selectFile(dropped);

    // Clear drag data to avoid potential browser quirks
    e.dataTransfer.clearData();
  };

  /** Handle file selection via hidden input. */
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    selectFile(selected);

    // Reset input value so selecting the same file again still triggers onChange
    e.target.value = '';
  };

  const handleOpenFilePicker = () => {
    inputRef.current?.click();
  };

  const handleClear = () => {
    validationToken.current++;
    setFile(null);
    setError(null);
    setValidating(false);
  };

  const handleConfirm = () => {
    if (!file || error || validating) return;
    onConfirmFile(file);
  };

  return (
    <Box
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleDrag}
      onDrop={handleDrop}
      sx={(theme) => ({
        border: '2px dashed',
        borderColor: dragActive ? 'primary.main' : theme.palette.divider,
        borderRadius: 2,
        p: 4,
        textAlign: 'center',
        bgcolor: dragActive ? 'action.hover' : 'background.default',
        transition: 'border-color 0.2s ease, background-color 0.2s ease',
        userSelect: 'none',
      })}
    >
      {/* Hidden native file input (single file) */}
      <input
        ref={inputRef}
        type="file"
        hidden
        onChange={handleChange}
        // Keep it generic; backend will validate template anyway.
        // accept=".xlsx,.xls"
      />

      {!file ? (
        <Stack spacing={1.5} alignItems="center">
          <Typography variant="body1">Drag & drop your Excel file here</Typography>
          <Typography variant="body2" color="text.secondary">
            or
          </Typography>
          <Button variant="outlined" onClick={handleOpenFilePicker}>
            Choose File
          </Button>
          <Typography variant="caption" color="text.secondary">
            Max {maxSizeMb}MB / {maxRows.toLocaleString()} rows.
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={1.5} alignItems="center">
          <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
            {file.name}
          </Typography>

          {error && (
            <Typography variant="body2" color="error.main">
              {error}
            </Typography>
          )}

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" color="inherit" onClick={handleClear}>
              Change
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={Boolean(error) || validating}
            >
              {validating ? 'Validating…' : 'Confirm Upload'}
            </Button>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            Upload will start after confirmation.
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

export default DragDropFile;
